/**
 * Protocole de synchronisation Hybride, côté poste (contrat : SYNC_DESIGN.md §2 à §5).
 * Les réponses du serveur sont validées avant usage : une réponse mal formée est une erreur transitoire
 * (jamais une perte de donnée).
 */
import { z } from 'zod';
import type { SyncEnvelope, SyncResourceName, SyncWireEntry } from './SyncBridgeTypes';

export const SYNC_PROTOCOL_VERSION = 1;
/** Version du schéma de données de synchronisation envoyée avec chaque lot. */
export const SYNC_CLIENT_SCHEMA_VERSION = 3;
export const SYNC_PROTOCOL_HEADER = 'X-Sync-Protocol';

export const SYNC_RESOURCES: readonly SyncResourceName[] = [
  'tenant-profile',
  'salaries',
  'centres-analytiques',
  'bulletins',
  'ecritures',
  'factures',
];

export const SYNC_LIMITS = {
  maxOpsPerPush: 50,
  maxPushBytes: 512 * 1024,
  maxPullPage: 1000,
  bootstrapPage: 500,
  applyChunk: 200,
} as const;

export interface SyncStatusResponse {
  protocol: number;
  server_time: string;
  tenant_id: string;
  epoch: string;
  head_cursor: string;
  sync_enabled: boolean;
  mode: 'read_write' | 'read_only';
  license: { status: string; expires_at?: string | null; days_remaining?: number | null; denial_code: string | null };
  min_client_version?: string;
  min_schema_version?: number;
  limits?: Record<string, number>;
  counts?: Record<string, number>;
  session?: { user_id: string; role: string };
}

export interface SyncOpResult {
  op_id: string;
  status: 'applied' | 'noop' | 'replayed' | 'rejected';
  outcome?: string;
  entity?: { resource: string; id: string; version: number; seq: number; updated_at?: string; deleted?: boolean };
  payload?: Record<string, unknown>;
  overwrote?: { version: number; origin_device?: string; at?: string };
  error?: { code: string; retry?: 'permanent' | 'transient' | 'conflict'; message?: string };
}

export interface SyncPushResponse {
  protocol: number;
  server_time: string;
  head_cursor: string;
  results: SyncOpResult[];
}

export interface SyncPullResponse {
  protocol: number;
  epoch: string;
  items: SyncEnvelope[];
  next_cursor: string;
  has_more: boolean;
  head_cursor: string;
  denied_resources?: string[];
  server_time?: string;
}

export interface SyncBootstrapResponse {
  resource: string;
  items: SyncEnvelope[];
  next_after: string | null;
  done: boolean;
  head_cursor: string;
}

export interface SyncPushRequest {
  protocol: number;
  schema_version: number;
  app_version: string;
  device_label?: string;
  pending_count?: number;
  oldest_pending_at?: string | null;
  ops: SyncWireOp[];
}

export type SyncWireOp = Omit<SyncWireEntry, 'seq' | 'attempts'>;

/** Le serveur peut envoyer null pour un champ facultatif (fiche issue d'une migration ou d'une restauration). */
function nullToUndefined<T extends z.ZodTypeAny>(schema: T) {
  return schema.nullish().transform((value) => value ?? undefined);
}

const envelopeSchema = z
  .object({
    resource: z.string(),
    id: z.string(),
    version: z.number().int(),
    seq: z.number().int(),
    updated_at: nullToUndefined(z.string()),
    deleted: nullToUndefined(z.boolean()),
    locked: nullToUndefined(z.boolean()),
    origin_device: nullToUndefined(z.string()),
    payload: nullToUndefined(z.record(z.string(), z.unknown())),
  })
  .passthrough();

const statusSchema = z
  .object({
    protocol: z.number().int(),
    server_time: z.string(),
    tenant_id: z.string(),
    epoch: z.string(),
    head_cursor: z.string(),
    sync_enabled: z.boolean(),
    mode: z.enum(['read_write', 'read_only']),
    license: z
      .object({
        status: z.string(),
        expires_at: z.string().nullable().optional(),
        days_remaining: z.number().nullable().optional(),
        denial_code: z.string().nullable(),
      })
      .passthrough(),
    min_client_version: z.string().optional(),
    min_schema_version: z.number().int().optional(),
    limits: z.record(z.string(), z.number()).optional(),
    counts: z.record(z.string(), z.number()).optional(),
    session: z.object({ user_id: z.string(), role: z.string() }).passthrough().optional(),
  })
  .passthrough();

const opResultSchema = z
  .object({
    op_id: z.string(),
    status: z.enum(['applied', 'noop', 'replayed', 'rejected']),
    outcome: z.string().optional(),
    entity: z
      .object({
        resource: z.string(),
        id: z.string(),
        version: z.number().int(),
        seq: z.number().int(),
        updated_at: z.string().optional(),
        deleted: z.boolean().optional(),
      })
      .passthrough()
      .optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
    overwrote: z.object({ version: z.number().int() }).passthrough().optional(),
    error: z
      .object({
        code: z.string(),
        retry: z.enum(['permanent', 'transient', 'conflict']).optional(),
        message: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const pushSchema = z
  .object({
    protocol: z.number().int(),
    server_time: z.string(),
    head_cursor: z.string(),
    results: z.array(opResultSchema),
  })
  .passthrough();

const pullSchema = z
  .object({
    protocol: z.number().int(),
    epoch: z.string(),
    items: z.array(envelopeSchema),
    next_cursor: z.string(),
    has_more: z.boolean(),
    head_cursor: z.string(),
    denied_resources: z.array(z.string()).optional(),
    server_time: z.string().optional(),
  })
  .passthrough();

const bootstrapSchema = z
  .object({
    resource: z.string(),
    items: z.array(envelopeSchema),
    next_after: z.string().nullable(),
    done: z.boolean(),
    head_cursor: z.string(),
  })
  .passthrough();

export class SyncProtocolError extends Error {
  constructor(what: string) {
    super(`Réponse de synchronisation invalide (${what})`);
    this.name = 'SyncProtocolError';
  }
}

function parseWith<T>(schema: z.ZodType<T>, raw: unknown, what: string): T {
  const parsed = schema.safeParse(raw);
  if (!parsed.success) throw new SyncProtocolError(what);
  return parsed.data;
}

export const parseStatusResponse = (raw: unknown): SyncStatusResponse => parseWith(statusSchema, raw, 'status') as SyncStatusResponse;
export const parsePushResponse = (raw: unknown): SyncPushResponse => parseWith(pushSchema, raw, 'push') as SyncPushResponse;
export const parsePullResponse = (raw: unknown): SyncPullResponse => parseWith(pullSchema, raw, 'pull') as SyncPullResponse;
export const parseBootstrapResponse = (raw: unknown): SyncBootstrapResponse =>
  parseWith(bootstrapSchema, raw, 'bootstrap') as SyncBootstrapResponse;

/** Curseur opaque `<epoch>:<seq>`. */
export function parseCursor(cursor: string | null | undefined): { epoch: string; seq: number } | null {
  if (!cursor) return null;
  const at = cursor.lastIndexOf(':');
  if (at <= 0) return null;
  const seq = Number(cursor.slice(at + 1));
  if (!Number.isInteger(seq) || seq < 0) return null;
  return { epoch: cursor.slice(0, at), seq };
}

/** Compare deux versions « a.b.c » ; -1 si a < b. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** Retire les champs internes du poste d'une opération avant envoi. */
export function toWireOp(entry: SyncWireEntry): SyncWireOp {
  const { seq: _seq, attempts: _attempts, ...op } = entry;
  void _seq;
  void _attempts;
  return op;
}
